import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import {
  Box, Button, Typography, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DrawIcon from '@mui/icons-material/Draw';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { InvoiceApi } from 'axiosApi/apis/invoice-api';
import { Configuration } from 'axiosApi/configuration';
import { DocumentSigningApiWrapper } from 'api/DocumentSigningApiWrapper';
import { fetchRemoteSettings, resolveSettings } from 'api/settings';
import PdfViewer from './PdfViewer';

const API_VERSION = '1.0';

type FieldType = 'signature' | 'initials' | 'text' | 'date' | 'signerName' | 'signerEmail' | 'current_date' | 'current_datetime';
type PageState = 'loading' | 'ready' | 'success' | 'rejected' | 'error' | 'expired' | 'alreadySigned';

interface SigningField {
  id: number;
  fieldType: FieldType;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  value?: string | null;
  idSigningDocumentSigner: number;
}

interface SignerData {
  id: number;
  name?: string;
  email: string;
  status: string;
  fields: SigningField[];
}

interface DocumentData {
  id: number;
  title: string;
  description?: string;
}

// ── Drawing canvas used for signature/initials fields ──────────────────────

function DrawingDialog({
  open, title, onSave, onCancel,
}: { open: boolean; title: string; onSave: (dataUrl: string) => void; onCancel: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (open) setIsEmpty(true);
  }, [open]);

  const getCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return ctx;
  };

  const pos = (e: { clientX: number; clientY: number }) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };

  const onDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    const ctx = getCtx(); if (!ctx) return;
    const p = pos(e.nativeEvent);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    setIsEmpty(false);
  };
  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = getCtx(); if (!ctx) return;
    const p = pos(e.nativeEvent);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const onUp = () => { drawing.current = false; };

  const onTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); drawing.current = true;
    const ctx = getCtx(); if (!ctx) return;
    const p = pos(e.touches[0]);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    setIsEmpty(false);
  };
  const onTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); if (!drawing.current) return;
    const ctx = getCtx(); if (!ctx) return;
    const p = pos(e.touches[0]);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };

  const handleClear = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
  };

  const handleSave = () => {
    const dataUrl = canvasRef.current?.toDataURL('image/png') ?? '';
    onSave(dataUrl);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff', touchAction: 'none' }}>
          <canvas
            ref={canvasRef}
            width={560}
            height={200}
            style={{ display: 'block', width: '100%', height: 'auto', cursor: 'crosshair' }}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onUp}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear} disabled={isEmpty}>Clear</Button>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isEmpty} startIcon={<DrawIcon />}>
          Use
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function PublicSigningPage() {
  const { token } = useParams<{ token: string }>();
  const toast = useToastMessageQueue();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [signer, setSigner] = useState<SignerData | null>(null);
  const [fields, setFields] = useState<SigningField[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [wrapper, setWrapper] = useState<DocumentSigningApiWrapper | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // active field being filled
  const [activeField, setActiveField] = useState<SigningField | null>(null);
  const [textInput, setTextInput] = useState('');

  const overlayRef = useRef<HTMLDivElement>(null); // kept for legacy, unused

  useEffect(() => {
    const init = async () => {
      try {
        const remote = await fetchRemoteSettings();
        const settings = resolveSettings(remote);
        const invoiceApi = new InvoiceApi(new Configuration(), settings.URL_INVOICE);
        const w = new DocumentSigningApiWrapper(invoiceApi);
        setWrapper(w);
        setFileUrl(w.getFileUrlByToken(API_VERSION, token!));

        const res = await w.getDocumentByToken(API_VERSION, token!);
        const data = res.data?.data ?? res.data;

        if (!data) { setPageState('error'); setErrorMessage('Document not found.'); return; }

        setDocument(data.document);

        const signerData: SignerData = { ...data.signer, fields: data.fields ?? [] };
        setSigner(signerData);

        const myFields: SigningField[] = (data.fields ?? []).map((f: any) => ({
          id: f.id,
          fieldType: f.fieldType,
          page: f.page,
          x: Number(f.x),
          y: Number(f.y),
          width: Number(f.width),
          height: Number(f.height),
          required: f.required,
          value: f.value,
          idSigningDocumentSigner: f.idSigningDocumentSigner,
        }));
        setFields(myFields);

        // pre-fill auto values and any already-saved values
        const initial: Record<number, string> = {};
        for (const f of myFields) {
          if (f.value) { initial[f.id] = f.value; continue; }
          if (f.fieldType === 'signerName' && data.signer?.name) initial[f.id] = data.signer.name;
          if (f.fieldType === 'signerEmail' && data.signer?.email) initial[f.id] = data.signer.email;
          if (f.fieldType === 'current_date') initial[f.id] = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
          if (f.fieldType === 'current_datetime') initial[f.id] = new Date().toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
        }
        setFieldValues(initial);
        setPageState('ready');

        // Load stored profile images/name and pre-fill empty fields
        try {
          const profileRes = await w.getSignerProfile(API_VERSION, token!);
          const profile = profileRes.data?.data ?? profileRes.data;
          if (profile) {
            setFieldValues(prev => {
              const updated = { ...prev };
              for (const f of myFields) {
                if (updated[f.id]) continue; // don't overwrite already-saved or auto-filled values
                if (f.fieldType === 'signature' && profile.signatureDataUrl) {
                  updated[f.id] = profile.signatureDataUrl;
                } else if (f.fieldType === 'initials' && profile.initialsDataUrl) {
                  updated[f.id] = profile.initialsDataUrl;
                } else if (f.fieldType === 'signerName' && profile.fullName) {
                  updated[f.id] = profile.fullName;
                }
              }
              return updated;
            });
            if (profile.signatureDataUrl || profile.initialsDataUrl) setProfileLoaded(true);
          }
        } catch {
          // profile load is best-effort; don't fail the page
        }
      } catch (err: any) {
        const code = err?.response?.data?.errors?.[0]?.errorCode ?? '';
        if (code === 'B02' || err?.response?.status === 406) {
          const msg: string = err?.response?.data?.errors?.[0]?.message ?? '';
          if (msg.toLowerCase().includes('expired')) setPageState('expired');
          else if (msg.toLowerCase().includes('already')) setPageState('alreadySigned');
          else { setPageState('error'); setErrorMessage(msg || 'Invalid signing link.'); }
        } else {
          setPageState('error');
          setErrorMessage('Failed to load document.');
        }
      }
    };
    if (token) init();
  }, [token]);

  const requiredFields = fields.filter(f => f.required);
  const allComplete = requiredFields.every(f => !!fieldValues[f.id]?.trim());
  const completedCount = requiredFields.filter(f => !!fieldValues[f.id]?.trim()).length;

  const AUTO_FILLED: FieldType[] = ['signerName', 'signerEmail', 'current_date', 'current_datetime'];

  const handleFieldClick = (field: SigningField) => {
    if (AUTO_FILLED.includes(field.fieldType)) return;
    setActiveField(field);
    setTextInput(fieldValues[field.id] ?? '');
  };

  const handleDrawSave = (dataUrl: string) => {
    if (!activeField) return;
    setFieldValues(prev => ({ ...prev, [activeField.id]: dataUrl }));
    setActiveField(null);
  };

  const handleTextSave = () => {
    if (!activeField) return;
    setFieldValues(prev => ({ ...prev, [activeField.id]: textInput }));
    setActiveField(null);
  };

  const handleSign = async () => {
    if (!wrapper || !allComplete) return;
    setSubmitting(true);
    try {
      const fieldPayload = Object.entries(fieldValues).map(([fieldId, value]) => ({
        fieldId: Number(fieldId),
        value,
      }));
      await wrapper.submitSignature(API_VERSION, token!, { fields: fieldPayload });
      setPageState('success');
    } catch (err: any) {
      toast.error({ body: err?.response?.data?.errors?.[0]?.message ?? 'Failed to submit signature.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!wrapper) return;
    try {
      await wrapper.rejectSigning(API_VERSION, token!, { reason: rejectReason });
      setRejectDialogOpen(false);
      setPageState('rejected');
    } catch (err: any) {
      toast.error({ body: err?.response?.data?.errors?.[0]?.message ?? 'Failed to reject document.' });
    }
  };

  const pageFields = fields.filter(f => f.page === currentPage); // unused now, kept for page nav
  const pages = [...new Set(fields.map(f => f.page))].sort((a, b) => a - b);

  // ── Terminal states ────────────────────────────────────────────────────────

  if (pageState === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (pageState === 'success') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
        <Typography variant="h5">Document Signed Successfully</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>Thank you for signing.</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Once all parties have signed, a completed copy will be emailed to you.
        </Typography>
      </Box>
    );
  }
  if (pageState === 'rejected') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CancelOutlinedIcon sx={{ fontSize: 72, color: 'error.main', mb: 2 }} />
        <Typography variant="h5">Document Rejected</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>You have rejected this document. You may close this window.</Typography>
      </Box>
    );
  }
  if (pageState === 'expired') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <Typography variant="h6">Signing Link Expired</Typography>
          <Typography>This signing link has expired. Please ask the document sender to resend the invitation.</Typography>
        </Alert>
      </Box>
    );
  }
  if (pageState === 'alreadySigned') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
        <Typography variant="h5">Already Signed</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>You have already signed this document.</Typography>
      </Box>
    );
  }
  if (pageState === 'error') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          <Typography variant="h6">Error</Typography>
          <Typography>{errorMessage || 'An error occurred loading the document.'}</Typography>
        </Alert>
      </Box>
    );
  }

  // ── Ready: PDF + overlay ───────────────────────────────────────────────────

  const isDrawField = activeField?.fieldType === 'signature' || activeField?.fieldType === 'initials';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, bgcolor: '#303030', color: 'white', flexShrink: 0 }}>
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>{document?.title}</Typography>
        <Typography variant="caption" sx={{ color: '#bbb' }}>
          Signing as <strong style={{ color: 'white' }}>{signer?.name ?? signer?.email}</strong>
        </Typography>
        {profileLoaded && (
          <Chip size="small" label="Using stored signature" sx={{ bgcolor: '#2e7d32', color: '#fff' }} />
        )}
        <Chip
          size="small"
          label={`${completedCount} / ${requiredFields.length} fields`}
          color={allComplete ? 'success' : 'warning'}
        />
      </Box>

      {/* PDF rendered page-by-page with field overlays */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {fileUrl && (
          <PdfViewer
            url={fileUrl}
            fields={fields.map(field => {
              const filled = !!fieldValues[field.id]?.trim();
              const isAuto = AUTO_FILLED.includes(field.fieldType);
              return {
                id: field.id,
                page: field.page,
                x: field.x,
                y: field.y,
                width: field.width,
                height: field.height,
                filled,
                onClick: () => !isAuto && handleFieldClick(field),
                renderContent: (field.fieldType === 'signature' || field.fieldType === 'initials') && filled ? (
                  <img
                    src={fieldValues[field.id]}
                    alt={field.fieldType}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '9px',
                      fontWeight: 700,
                      color: filled ? 'success.dark' : 'primary.main',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      px: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    {filled
                      ? (isAuto ? fieldValues[field.id] : '✓')
                      : field.fieldType.toUpperCase()
                    }
                  </Typography>
                ),
              };
            })}
          />
        )}
      </Box>

      {/* Bottom bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, bgcolor: '#303030', flexShrink: 0 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={() => setRejectDialogOpen(true)}
          sx={{ borderColor: '#e57373', color: '#e57373' }}
        >
          Decline
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!allComplete || submitting}
          onClick={handleSign}
        >
          {submitting ? 'Submitting…' : 'Sign Document'}
        </Button>
      </Box>

      {/* Drawing dialog (signature / initials) */}
      <DrawingDialog
        open={!!activeField && isDrawField}
        title={activeField?.fieldType === 'signature' ? 'Draw Signature' : 'Draw Initials'}
        onSave={handleDrawSave}
        onCancel={() => setActiveField(null)}
      />

      {/* Text / date input dialog */}
      <Dialog open={!!activeField && !isDrawField} onClose={() => setActiveField(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {activeField?.fieldType === 'date' ? 'Enter Date' : 'Enter Text'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            type={activeField?.fieldType === 'date' ? 'date' : 'text'}
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            InputLabelProps={activeField?.fieldType === 'date' ? { shrink: true } : undefined}
            onKeyDown={e => { if (e.key === 'Enter') handleTextSave(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveField(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleTextSave} disabled={!textInput.trim()}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Decline Document</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Are you sure you want to decline signing this document?</Typography>
          <TextField
            label="Reason (optional)"
            multiline rows={3} fullWidth
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Decline</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
